import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Trophy, TrendingUp, Users, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAT_LABELS = {
  pts: 'Points', reb: 'Rebounds', ast: 'Assists', stl: 'Steals', blk: 'Blocks',
  fgm: 'Field Goals Made', three_m: '3-Pointers Made', ftm: 'Free Throws Made',
  oreb: 'Off Rebounds', dreb: 'Def Rebounds', to: 'Turnovers'
};

function RankingTable({ stat, players }) {
  if (!players || players.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-0">
        <div className="bg-[#00247D] px-3 py-2 rounded-t-lg">
          <h4 className="text-white text-xs font-bold uppercase">{STAT_LABELS[stat] || stat}</h4>
        </div>
        <div className="divide-y divide-slate-100">
          {players.slice(0, 10).map((p, i) => (
            <div key={p.player_id} className={`flex items-center px-3 py-2 text-sm ${i < 3 ? 'bg-amber-50/50' : ''}`}>
              <span className={`w-6 text-center font-bold ${i === 0 ? 'text-amber-500' : 'text-slate-400'}`}>{i + 1}</span>
              <span className="font-mono text-xs text-slate-400 w-8">#{p.jersey}</span>
              <span className="flex-1 font-medium text-slate-700 truncate">{p.name}</span>
              <span className="text-xs text-slate-400 mr-3">{p.team_name}</span>
              <span className="font-bold text-[#00247D] w-10 text-right">{p.value}</span>
              <span className="text-xs text-slate-400 w-14 text-right">{p.per_game}/g</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function HeadToHead({ token }) {
  const [teams, setTeams] = useState([]);
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/stats/head-to-head`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setTeams(d.teams || []));
  }, [token]);

  const compare = async () => {
    if (!teamA || !teamB) return;
    const res = await fetch(`${API_URL}/api/stats/head-to-head?team_a_name=${encodeURIComponent(teamA)}&team_b_name=${encodeURIComponent(teamB)}`,
      { headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) setResult(await res.json());
  };

  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="text-sm font-bold text-[#00247D] uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
          <Users className="w-4 h-4 inline mr-1" /> Head-to-Head Matchup History
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          <select value={teamA} onChange={e => setTeamA(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1 min-w-[140px]" data-testid="h2h-team-a">
            <option value="">Select Team A</option>
            {teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="self-center text-slate-400 font-bold">vs</span>
          <select value={teamB} onChange={e => setTeamB(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1 min-w-[140px]" data-testid="h2h-team-b">
            <option value="">Select Team B</option>
            {teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button size="sm" onClick={compare} disabled={!teamA || !teamB} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="h2h-compare-btn">
            Compare
          </Button>
        </div>
        {result && (
          <div>
            <div className="flex items-center justify-center gap-6 py-4 bg-slate-50 rounded-lg mb-4">
              <div className="text-center">
                <div className="text-xs text-slate-500 uppercase">{result.team_a}</div>
                <div className="text-3xl font-black text-[#00247D]">{result.series_record.team_a_wins}</div>
              </div>
              <div className="text-xs text-slate-400">-</div>
              <div className="text-center">
                <div className="text-xs text-slate-500 uppercase">{result.team_b}</div>
                <div className="text-3xl font-black text-[#CF142B]">{result.series_record.team_b_wins}</div>
              </div>
            </div>
            {result.matchups.length === 0 ? (
              <p className="text-sm text-slate-400 text-center">No games found between these teams</p>
            ) : (
              <div className="space-y-2">
                {result.matchups.map(m => (
                  <div key={m.game_id} className="flex items-center justify-between p-2 bg-white border border-slate-100 rounded text-sm" data-testid={`h2h-game-${m.game_id}`}>
                    <span className="text-slate-500">{m.date}</span>
                    <span className="font-medium">{m.game_name}</span>
                    <span className="font-bold text-[#00247D]">{m.team_a_score} - {m.team_b_score}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${m.winner === result.team_a ? 'bg-blue-100 text-blue-700' : m.winner === result.team_b ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                      {m.winner}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LineupRankings() {
  const navigate = useNavigate();
  const { isAuthenticated, isPro, isLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rankings');
  const [eventFilter, setEventFilter] = useState('all');
  const [seasonFilter, setSeasonFilter] = useState('all');
  // Keep a stable copy of the filter option lists so the dropdowns don't
  // collapse when the currently active filter narrows the response payload.
  const [availableEvents, setAvailableEvents] = useState([]);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const token = localStorage.getItem('session_token');

  const fetchRankings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (eventFilter && eventFilter !== 'all') params.set('event_id', eventFilter);
      if (seasonFilter && seasonFilter !== 'all') params.set('season_id', seasonFilter);
      const qs = params.toString();
      const res = await fetch(`${API_URL}/api/stats/lineup-rankings${qs ? `?${qs}` : ''}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        // Only refresh the option lists from an *unfiltered* response so the
        // dropdowns keep every league/season even when a filter is active.
        if (eventFilter === 'all' && seasonFilter === 'all') {
          setAvailableEvents(d.available_events || []);
          setAvailableSeasons(d.available_seasons || []);
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [eventFilter, seasonFilter, token]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated || !isPro) { navigate('/stats'); return; }
    fetchRankings();
  }, [isAuthenticated, isPro, isLoading, fetchRankings, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/stats')} className="px-2 sm:px-3 shrink-0"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              <span className="hidden sm:inline">PLAYER ANALYTICS</span>
              <span className="sm:hidden">ANALYTICS</span>
            </h1>
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 shrink-0">
            {[
              { key: 'rankings', label: 'Rankings', icon: Trophy },
              { key: 'h2h', label: 'Head-to-Head', icon: Users },
            ].map(t => (
              <button key={t.key}
                className={`px-2 sm:px-3 py-1 text-xs font-bold rounded flex items-center gap-1 ${activeTab === t.key ? 'bg-white shadow text-[#00247D]' : 'text-slate-500'}`}
                onClick={() => setActiveTab(t.key)} data-testid={`tab-${t.key}`}
                aria-label={t.label}>
                <t.icon className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {activeTab === 'rankings' && (
          <>
            {/* League + Season scope filters. Only shown on Rankings tab —
                Head-to-Head has its own scoping via team pickers. */}
            <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="rankings-filter-row">
              <div className="inline-flex items-center gap-2 text-slate-500 text-sm">
                <Filter className="w-4 h-4" />
                <span className="font-medium">Filter:</span>
              </div>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-full sm:w-[240px] h-9" data-testid="tournament-filter-select">
                  <SelectValue placeholder="All Tournaments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="tournament-filter-all">All Tournaments</SelectItem>
                  {availableEvents.map(ev => (
                    <SelectItem key={ev.id} value={ev.id} data-testid={`tournament-filter-${ev.id}`}>
                      {ev.name} {ev.games_count ? `(${ev.games_count})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={seasonFilter} onValueChange={setSeasonFilter}>
                <SelectTrigger className="w-full sm:w-[200px] h-9" data-testid="season-filter-select">
                  <SelectValue placeholder="All Seasons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="season-filter-all">All Seasons</SelectItem>
                  {availableSeasons.map(s => (
                    <SelectItem key={s.id} value={s.id} data-testid={`season-filter-${s.id}`}>
                      {s.name} {s.games_count ? `(${s.games_count})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableEvents.length === 0 && availableSeasons.length === 0 && (
                <span className="text-xs text-slate-400">
                  Link games to a tournament or season at setup to enable filtering.
                </span>
              )}
            </div>

            {!data || Object.keys(data.rankings).length === 0 ? (
              <div className="text-center py-16" data-testid="empty-state">
                <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {eventFilter !== 'all' || seasonFilter !== 'all'
                    ? 'No completed games match this filter.'
                    : 'Complete some games to see lineup rankings.'}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-6">{data.games_count} game{data.games_count !== 1 ? 's' : ''} analyzed</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rankings-grid">
                  {Object.entries(data.rankings).filter(([, v]) => v.length > 0).map(([stat, players]) => (
                    <RankingTable key={stat} stat={stat} players={players} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
        {activeTab === 'h2h' && <HeadToHead token={token} />}
      </div>
    </div>
  );
}
